import type _jquery from "jquery";
import { computed, provide, type ComputedRef, type DeepReadonly, readonly } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";

import { useFormSettings } from "./use-form-settings.js";
import { useGatingLevel } from "./use-gating-level.js";
import { UserMarketoInputName, HiddenMarketoInputName, AnyMarketoInputName } from "../types/definitions/inputs/marketo-input-names-and-values.js";
import { isStringArray } from "@mcwd/typescript-type-guards";
import { useFormLogger } from "./use-form-logger.js";
import type { GatingState } from "../types/definitions/gating/gating-state.js";

type UseEnabledFieldsInjectionValues<
  TEnabledUserField extends string = string,
  TEnabledHiddenField extends string = string
> = InjectionValuesRecord<{
  allEnabledFields: DeepReadonly<ComputedRef<Array<TEnabledUserField | TEnabledHiddenField>>>,
  enabledUserFields: DeepReadonly<ComputedRef<Array<TEnabledUserField>>>,
  enabledHiddenFields: DeepReadonly<ComputedRef<Array<TEnabledHiddenField>>>
}>;

type UseEnabledFieldsInjectionKeys<
  TEnabledUserField extends string = string,
  TEnabledHiddenField extends string = string
> = InjectionKeysRecord<UseEnabledFieldsInjectionValues<TEnabledUserField, TEnabledHiddenField>>;

const UseEnabledFieldsInjectionKeys = CreateInjectionKeysRecord<UseEnabledFieldsInjectionKeys>({
  allEnabledFields: "allEnabledFields",
  enabledUserFields: "enabledUserFields",
  enabledHiddenFields: "enabledHiddenFields",
});


export function initEnabledFields(){
  if (checkValuesAlreadyProvided(UseEnabledFieldsInjectionKeys)) {
    return;
  }
  const { FormSettings } = useFormSettings({ callerLabel: "setFormUserInputValuesFromSettings" });
  const { CurrentMarketoFormGatingState } = useGatingLevel({ callerLabel: "setFormUserInputValuesFromSettings" });

  const enabledFieldsObj = FormSettings.enabledFields as unknown as { [key in GatingState.MarketoForm]?: AnyMarketoInputName };

  const allEnabledFields = computed(() => {
    const gatingLevel = CurrentMarketoFormGatingState.value;
    if (gatingLevel in enabledFieldsObj) {
      const fields = enabledFieldsObj[gatingLevel];
      if (isStringArray(fields)) {
        return fields as AnyMarketoInputName[];
      }
    }
    throw new Error(
      `Gating State for "${gatingLevel}" does not exist in the enabled fields object for formExperience '${FormSettings.formSettingsKey}'`
    );
  });

  const enabledUserFields = computed<UserMarketoInputName[]>(() => {
    return allEnabledFields.value.filter(f =>
      (UserMarketoInputName as readonly string[]).includes(f)
    ) as UserMarketoInputName[];
  });

  const enabledHiddenFields = computed<HiddenMarketoInputName[]>(() => {
    return allEnabledFields.value.filter(f =>
      (HiddenMarketoInputName as readonly string[]).includes(f)
    ) as HiddenMarketoInputName[];
  });

  provide(UseEnabledFieldsInjectionKeys.allEnabledFields, readonly(allEnabledFields));
  provide(UseEnabledFieldsInjectionKeys.enabledUserFields, readonly(enabledUserFields));
  provide(UseEnabledFieldsInjectionKeys.enabledHiddenFields, readonly(enabledHiddenFields));
}

export function useEnabledFields<TEnabledFields extends AnyMarketoInputName>({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useEnabledFields";

    const allEnabledFields = injectRequiredWithSelf(
      UseEnabledFieldsInjectionKeys.allEnabledFields as UseEnabledFieldsInjectionKeys<
        Extract<TEnabledFields, UserMarketoInputName>,
        Extract<TEnabledFields, HiddenMarketoInputName>
      >["allEnabledFields"]
    );
    const enabledUserFields = injectRequiredWithSelf(
      UseEnabledFieldsInjectionKeys.enabledUserFields as UseEnabledFieldsInjectionKeys<
        Extract<TEnabledFields, UserMarketoInputName>,
        Extract<TEnabledFields, HiddenMarketoInputName>
      >["enabledUserFields"]
    );
    const enabledHiddenFields = injectRequiredWithSelf(
      UseEnabledFieldsInjectionKeys.enabledHiddenFields as UseEnabledFieldsInjectionKeys<
        Extract<TEnabledFields, UserMarketoInputName>,
        Extract<TEnabledFields, HiddenMarketoInputName>
      >["enabledHiddenFields"]
    );
    return {
      allEnabledFields,
      enabledUserFields,
      enabledHiddenFields
    };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}